/**
 * The ONLY place kitchen-ticket strings are produced.
 *
 * Every string below exists twice in the fleet today and the two copies
 * disagree. Suppression is the one that hurts: the gateway matches six prefixes
 * (apps/gateway/internal/renderer/escpos_kitchen.go:272-280) and the POS matches
 * two (apps/pos/lib/services/printer/receipt_command_builder.dart:1320-1322), so
 * "WITHOUT NUTS" prints bold-underlined-red on the gateway and as an ordinary
 * grey modifier on the till. An allergy instruction that loses its emphasis on
 * one tier is not a formatting difference.
 *
 * WHY `packages/models` AND NOT `packages/common`: dependencies flow upward only
 * (`models → schemas / common → core / client → apps`), and it is real rather
 * than aspirational — `packages/common/package.json` declares
 * `@order-up/models: workspace:*` while `packages/models/package.json` declares
 * no `@order-up` dependency at all. The kitchen ticket engine (packages/core)
 * and the composers (packages/common) both read this grammar, and the preview
 * lays `measure` blocks out through `measureLines` below; a models home is the
 * one place all of them can reach. These functions are
 * pure string/format operations over model types with no other dependency, so
 * models is their correct home on the merits as well. `packages/common`
 * re-exports them from BOTH `index.ts` (browser preview) and `node.ts` (server
 * compose), so every composer keeps importing from `@order-up/common` and
 * `packages/core` and `packages/client` — siblings that cannot see each other —
 * read one grammar.
 */
import type { PrintCourseState, PrintMeasureBlock } from './PrintDocument';
import { PRINT_MEASURE_COLUMNS_TOKEN } from './PrintDocument';
import { formatPhoneForDisplay } from '../primitives/formatPhone';
import type { TicketModifierInput } from './PrintDocumentInputs';

/**
 * The six suppression prefixes, verbatim from escpos_kitchen.go:274.
 *
 * The trailing spaces on NO/REMOVE/WITHOUT/SUB are load-bearing: without them
 * "NOODLES" and "SUBMARINE ROLL" classify as allergy instructions and print in
 * the alert colour, which is how a red line stops meaning anything.
 */
export const SUPPRESSION_PREFIXES = [
  'NO ',
  'REMOVE ',
  'WITHOUT ',
  'ALLERGY',
  'ALLERGEN',
  'SUB ',
] as const;

/**
 * Typographic folding, mirroring the gateway's replacer
 * (apps/gateway/internal/renderer/text_layout.go:61-75). Only the entries that
 * can change a printed column or a prefix match are reproduced; the rest of the
 * gateway's table is code-page work the emitter owns.
 */
const TYPOGRAPHIC: ReadonlyArray<readonly [RegExp, string]> = [
  [/[×✕✖⨯]/g, 'x'],
  [/[—–‒―−]/g, '-'],
  [/[“”„‟]/g, '"'],
  [/[‘’‚‛′]/g, "'"],
  [/…/g, '...'],
  [/[•▪◦‣]/g, '*'],
  [/→/g, '->'],
  [/←/g, '<-'],
  [/⇒/g, '=>'],
  // Invisible characters as escapes: a literal one in source is unreviewable.
  [/[\u00a0\u2007\u2009\u202f\u2002\t]/g, ' '],
  // The zero-width set is an ALTERNATION, not a character class: U+200D inside
  // a class trips `no-misleading-character-class`, whose whole point is that a
  // class holding a joiner can match half a grapheme cluster. Each alternative
  // is a single code unit, so this matches exactly what the class did.
  [/\u200b|\u200c|\u200d|\ufeff|\r/g, ''],
];

const PRINTABLE_ASCII = /^[\x20-\x7e\n]*$/;
// eslint-disable-next-line no-control-regex -- neutralising control bytes is the point
const CONTROL = /[\x00-\x09\x0b-\x1f\x7f]/g;

/**
 * Reduce text to what a thermal head prints predictably: one rune in, one
 * column out, no byte that could be read as part of an ESC/POS command.
 * Idempotent, so it is safe to call at every layer — and every layer does.
 */
export function sanitizeTicketText(value: string): string {
  if (PRINTABLE_ASCII.test(value)) return value;
  let out = value;
  for (const [pattern, replacement] of TYPOGRAPHIC) {
    out = out.replace(pattern, replacement);
  }
  return out.replace(CONTROL, ' ');
}

/**
 * Does this modifier REMOVE something rather than add it?
 *
 * Same normalisation as the gateway: sanitize, trim, upper-case, then prefix
 * test (escpos_kitchen.go:273).
 */
export function classifySuppressionModifier(name: string): boolean {
  const upper = sanitizeTicketText(name).trim().toUpperCase();
  return SUPPRESSION_PREFIXES.some((prefix) => upper.startsWith(prefix));
}

function titleCase(value: string): string {
  return value
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Leading/trailing ornament runs. An operator who names a course
 * "*** Specials ***" or "--- Desserts ---" is typing a banner, not a name, and
 * that name would otherwise reach paper as a SECOND banner grammar beside the
 * emitter's own. Interior punctuation is untouched — "Pre-Fixe" is a course.
 *
 * Trimmed by WALKING the ends, not by `/^[\s*\-=_]+|[\s*\-=_]+$/g`. That
 * expression is a polynomial ReDoS (CodeQL `js/polynomial-redos`): the trailing
 * alternative carries no left anchor, so on a name like `A` + many spaces + `B`
 * the engine retries the run from every offset and the cost is quadratic. This
 * runs once per course per ticket, on the API and on every till, over a string
 * an operator types — so it is reachable by accident, not only by attack. The
 * walk is linear and its output is identical, which is what the existing
 * ornament tests pin.
 */
const ORNAMENT_PUNCTUATION = '*-=_';

/** `\s` is tested ONE character at a time, so there is nothing to backtrack. */
function isOrnamentEdgeChar(char: string): boolean {
  return ORNAMENT_PUNCTUATION.includes(char) || /\s/.test(char);
}

function stripOrnamentEdges(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && isOrnamentEdgeChar(value[start]!)) start += 1;
  while (end > start && isOrnamentEdgeChar(value[end - 1]!)) end -= 1;
  return start === 0 && end === value.length ? value : value.slice(start, end);
}

/**
 * THE banner grammar. "FIRE Appetizers" / "HOLD Entrees" / "FIRE" for
 * uncategorised items. This is the only TypeScript definition of it — the spec
 * pins both the signature and the rule ("produced only by courseBannerText",
 * spec:2356 and :2705) — and every other caller in this change set imports it
 * from `@order-up/common` rather than restating it.
 *
 * The "--- … ---" ornament is applied by the EMITTER, so the ornament and the
 * words cannot drift apart. The "*** FIRE ENTREES ***" star form is dropped:
 * one grammar, one place. `state` is the pinned `PrintCourseState`; there is
 * deliberately no second `CourseBannerState` union.
 */
export function courseBannerText(name: string, state: PrintCourseState): string {
  const courseName = titleCase(stripOrnamentEdges(sanitizeTicketText(name)));
  if (state === 'none') return courseName;
  const stateWord = state === 'fire' ? 'FIRE' : 'HOLD';
  return courseName.length > 0 ? `${stateWord} ${courseName}` : stateWord;
}

/**
 * How a modifier's COUNT is said, on every surface that says it: `''` for one,
 * `'2x'` for two. The kitchen chit and the guest receipt both print the count
 * and they must print it the same way, so the rule is written once here rather
 * than twice in two composers that nobody diffs against each other.
 *
 * A count of 0 or a non-finite one reads as one — a modifier that is on the
 * line at all was selected at least once, and printing `0x Tamago` beside a
 * charge for it is worse than printing no count.
 */
export function modifierQtyPrefix(qty: number | undefined): string {
  const n = qty ?? 1;
  if (!Number.isFinite(n) || n <= 1) return '';
  return `${Math.trunc(n)}x`;
}

/**
 * The modifier line, fully formed. Modifiers print VERBATIM downstream
 * (escpos_kitchen.go:249-251) — operators encode their own conventions in the
 * names — so every prefix an operator did not type is added exactly here.
 */
export function modifierDisplayText(mod: TicketModifierInput): string {
  const name = sanitizeTicketText(mod.name).trim();
  const price = mod.priceMinor ?? 0;
  const parts: string[] = [];
  const qtyPrefix = modifierQtyPrefix(mod.qty);
  if (qtyPrefix) parts.push(qtyPrefix);
  if (Number.isFinite(price) && price > 0) parts.push('$');
  parts.push(name);
  return parts.join(' ');
}

/**
 * The modifier name as a RECEIPT prints it — the count, then the name.
 *
 * 🔴 THE COUNT IS THE WHOLE POINT, AND THE PRICE IS ALREADY EXTENDED.
 * `OrderModifier.priceDelta` is the SUM of a modifier's per-instance deltas,
 * never price × qty (modifier_pricing_helper.dart:198) — so a 2× Tamago at
 * $3.50 arrives here as ONE modifier with `priceMinor: 700`. The money on the
 * paper has always been right; the COUNT was never printed, so a receipt read
 * "Tamago +$7.00" and the guest had no way to tell two from one mispriced.
 * Live on House of Pho order #54, 2026-09-19 — the till showed 2, the paper,
 * the chit and the management report each showed nothing.
 *
 * No `$` marker, unlike the chit's: a receipt prints the actual amount in its
 * own column, so the marker the kitchen uses to mean "this one costs extra"
 * would be noise next to the number it stands in for.
 *
 * Unlike the chit, this counts a SUPPRESSION too ("2x No Onion"). No receipt
 * renderer classifies suppression — there is no emphasis to protect here — so
 * saying what was selected costs nothing. The chit's exception exists because
 * two tiers there classify the COMPOSED STRING and would lose an allergy
 * line's emphasis (kitchenOrchestrationService.ts). The asymmetry is
 * deliberate and lives in exactly those two comments.
 */
export function salesModifierDisplayText(mod: {
  readonly name: string;
  readonly qty?: number;
}): string {
  const name = sanitizeTicketText(mod.name).trim();
  const qtyPrefix = modifierQtyPrefix(mod.qty);
  return qtyPrefix ? `${qtyPrefix} ${name}` : name;
}

/**
 * Integer minor units to a printable amount. The TS twin of `formatCents`
 * (escpos_format.go:36-41) and of the Dart `formatMinorUnits`
 * (apps/pos/lib/services/print/compose/document_builder.dart).
 *
 * MINOR UNITS IN, FORMATTED STRING OUT — the unit question is removed rather
 * than answered twice. It WAS answered twice: the gateway payload is explicit
 * (`Amount int64 // cents`, `BalanceAfter *int64 // cents`, printjob/types.go)
 * while the Dart builder's header says "Amounts in the map are in DOLLARS"
 * (receipt_command_builder.dart:576-579) and formats the raw number with
 * `toStringAsFixed(2)`, so one $25.00 gift card printed `CARD BALANCE $25.00`
 * through the gateway and `CARD BALANCE $2500.00` in reverse video on the
 * direct-Epson tier. Every composer formats money here and nowhere else.
 *
 * A non-USD code prints as a CODE and a space ("EUR 25.00"), never a symbol:
 * PC850 is the only code page both fleet models support and it cannot render
 * most currency symbols, so a symbol reaches paper as a substitution glyph.
 * Byte-for-byte the Dart twin's rule.
 *
 * A non-finite amount formats as zero rather than "$NaN", and the function
 * never throws: a composer that throws prints nothing at all, and a blank
 * where the total goes is worse than a visibly wrong zero.
 */
export function formatMinorUnits(minor: number, currency?: string): string {
  const symbol = currency == null || currency === 'USD' ? '$' : `${currency} `;
  const whole = Number.isFinite(minor) ? Math.trunc(minor) : 0;
  const abs = Math.abs(whole);
  const text = `${symbol}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return whole < 0 ? `-${text}` : text;
}

/**
 * A zone string `Intl` will accept. Pattern-then-construct, because the
 * constructor is the only authority on what the runtime's ICU actually carries
 * and it THROWS on a zone it does not know.
 *
 * This repeats `isValidIanaTz` (packages/common/src/utils/timezone.ts:21) rather
 * than importing it, and the repetition is forced: models may never import
 * common. It is four lines of pure `Intl` with no product rule in it — the
 * grammar this file exists to unify is the ticket text, not zone validation.
 */
const IANA_TZ_PATTERN = /^[A-Za-z_]+(?:\/[A-Za-z_+-][A-Za-z0-9_+-]*)*$/;

function resolvePrintableZone(timeZone: string): string {
  if (!timeZone || !IANA_TZ_PATTERN.test(timeZone)) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * "7:19 PM" — no leading zero. Twin of kitchenClock (escpos_kitchen.go:161-163)
 * and _kitchenClock (receipt_command_builder.dart:1369-1374).
 *
 * A bad zone falls back to UTC rather than throwing: a ticket with the wrong
 * clock still gets the food out, a composer that throws prints nothing at all.
 *
 * The U+202F strip is not cosmetic: ICU 72+ separates the minute from the
 * meridiem with a NARROW NO-BREAK SPACE, which sanitizeTicketText would fold to
 * a plain space anyway — doing it here keeps the two tiers byte-identical no
 * matter which Node/ICU build composed the ticket.
 */
export function formatTicketClock(epochMs: number, timeZone: string): string {
  if (!Number.isFinite(epochMs)) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolvePrintableZone(timeZone),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(epochMs))
    .replace(/[\u202f\u00a0]/g, ' ');
}

// ─── Where the order came from, and how it leaves ────────────────────────────
//
// A production chit carried the ORDER TYPE and nothing else, and only on the
// legacy tier — the document composer dropped even that. So a $9 pickup the
// restaurant keeps every cent of and a $9 Uber Eats order it pays 30% on
// printed as the same piece of paper, and the line had no way to tell a chit
// that needs bagging with a marketplace label from one a guest is standing at
// the counter for.
//
// The labels below are the ONLY definition of that vocabulary in TypeScript.
// The Go gateway and the Dart till each hold a mirror, pinned by
// `orderChannelGrammar` fixtures on all three tiers, for the same reason
// `courseBannerText` is pinned: three renderers producing three spellings of
// "TAKE OUT" is how one restaurant's chits stop looking like one document.

/**
 * `OrderType` → the words on paper.
 *
 * Keyed on the model's own union (`packages/models/src/orders/types.ts`) but
 * typed as a plain record, because this file is reached from the print wire
 * where the value has already been through JSON and is a bare string. An
 * unrecognised type upper-cases and prints — a chit that names a type nobody
 * mapped is still readable, a blank one is not.
 */
const ORDER_TYPE_LABELS: Readonly<Record<string, string>> = {
  dineIn: 'DINE IN',
  takeOut: 'TAKE OUT',
  delivery: 'DELIVERY',
  quickService: 'QUICK SERVICE',
  // Not in `OrderType` today. The gateway has rendered both since before that
  // union existed, and a value already on the wire must not lose its spelling
  // just because this map was written from the union.
  driveThru: 'DRIVE THRU',
  curbside: 'CURBSIDE',
};

/**
 * `ExternalOrderSource` → the marketplace's own name, spelled the way the
 * marketplace spells it.
 *
 * `internal` is deliberately absent: it means "our own rail", which is what
 * `orderSource` already says, and a chit reading "INTERNAL - DELIVERY" names
 * nothing a cook can act on.
 */
const EXTERNAL_SOURCE_LABELS: Readonly<Record<string, string>> = {
  ubereats: 'UBER EATS',
  doordash: 'DOORDASH',
  grubhub: 'GRUBHUB',
  chownow: 'CHOWNOW',
  foodhub: 'FOODHUB',
  gloriafood: 'GLORIAFOOD',
  flipdish: 'FLIPDISH',
  beyondmenu: 'BEYONDMENU',
  cloudwaitress: 'CLOUDWAITRESS',
  eatstreet: 'EATSTREET',
  menufy: 'MENUFY',
  kitchenhub: 'KITCHENHUB',
  square: 'SQUARE',
  toast: 'TOAST',
};

/**
 * `OrderSource` → the words on paper, for the sources that are not a named
 * marketplace.
 *
 * `pos` maps to the empty string ON PURPOSE. An order rung on the till in the
 * room is the default case, and stamping "POS" on every dine-in chit in every
 * restaurant is noise that trains the line to stop reading the header — the
 * exact failure the brand banner and the station banner are guarded against.
 * `thirdParty` is the honest fallback for an aggregator order whose marketplace
 * we could not map, which `ExternalOrderSource` says explicitly is a case that
 * happens.
 */
const ORDER_SOURCE_LABELS: Readonly<Record<string, string>> = {
  pos: '',
  online: 'ONLINE',
  thirdParty: 'THIRD PARTY',
  kiosk: 'KIOSK',
  phone: 'PHONE',
  api: 'API',
};

/**
 * WHERE the order came from, in one word or two.
 *
 * The marketplace wins when there is one: "UBER EATS" tells the line which app
 * the courier is holding, while "THIRD PARTY" tells them only that it is not
 * theirs. Falls back to the channel, then to the raw value upper-cased, then to
 * nothing — an unmapped source prints what it is rather than disappearing.
 */
export function orderChannelLabel(orderSource?: string, externalSource?: string): string {
  const external = sanitizeTicketText(externalSource ?? '').trim();
  if (external.length > 0 && external !== 'internal') {
    return EXTERNAL_SOURCE_LABELS[external] ?? external.toUpperCase();
  }
  const source = sanitizeTicketText(orderSource ?? '').trim();
  if (source.length === 0) return '';
  const mapped = ORDER_SOURCE_LABELS[source];
  return mapped !== undefined ? mapped : source.toUpperCase();
}

/** The order type, in the one spelling every tier prints. */
export function orderTypeLabel(orderType: string): string {
  const type = sanitizeTicketText(orderType ?? '').trim();
  if (type.length === 0) return '';
  return ORDER_TYPE_LABELS[type] ?? type.replace(/_/g, ' ').toUpperCase();
}

/**
 * THE channel line: "UBER EATS - DELIVERY", "ONLINE - TAKE OUT", "DELIVERY".
 *
 * One string rather than two blocks, because the two halves answer one question
 * — where the food is going and who it is going through — and a chit that can
 * print either half alone eventually prints a channel with no type on it.
 *
 * Either half may be empty: a POS order has no channel and prints just the
 * type, and an order whose type never reached the wire prints just the channel.
 * Both empty prints nothing at all.
 */
export function ticketChannelLine(
  orderSource: string | undefined,
  externalSource: string | undefined,
  orderType: string,
): string {
  const channel = orderChannelLabel(orderSource, externalSource);
  const type = orderTypeLabel(orderType);
  if (channel.length > 0 && type.length > 0) return `${channel} - ${type}`;
  return channel.length > 0 ? channel : type;
}

/**
 * A phone number a cook can dial off the paper.
 *
 * NANP shapes only — 10 digits, or 11 beginning with 1 — because those are the
 * only ones whose grouping is unambiguous. Everything else prints VERBATIM:
 * an international number regrouped by a rule written for North America is a
 * number that cannot be dialled, which is worse than an unformatted one.
 */
export function formatTicketPhone(raw: string): string {
  return formatPhoneForDisplay(sanitizeTicketText(raw ?? '').trim());
}

/**
 * The masked-number extension as it prints, BESIDE the phone number.
 *
 * Frozen wording. MIRRORS `phonePinSuffix` in
 * `apps/gateway/internal/renderer/escpos_kitchen.go` and in the POS composer —
 * three tiers, one form, because the same order can print through any of them.
 *
 * It lives here, beside `formatTicketPhone`, because it is never used without
 * it: the two compose one line. A PIN on a line of its own is unusable (no
 * number to dial it against) and a masked number without it does not connect,
 * so every caller writes `formatTicketPhone(n) + phonePinSuffix(p)` and the
 * leading double space is part of that contract rather than the caller's job.
 *
 * Both the kitchen chit and the merchant receipt draw it — see
 * `receiptCustomerContactBlocks` in the sales composer.
 */
export function phonePinSuffix(pin: string | undefined): string {
  const trimmed = sanitizeTicketText(pin ?? '').trim();
  return trimmed.length > 0 ? `  PIN ${trimmed}` : '';
}

/**
 * A flat delivery address, as PRINTABLE LINES.
 *
 * No renderer on any tier wraps a header line, so the split has to happen
 * before the address reaches a composer — and it has to happen the SAME way on
 * the server and on the till, or one Uber Eats order prints a two-line address
 * through the gateway and a three-line one when the POS composes it offline.
 *
 * A comma split is the only structure a flat address offers. The last TWO
 * fragments are rejoined ("Stratford", "CT 06614" -> "Stratford, CT 06614")
 * because a locality broken across two lines reads as two different places.
 *
 * A caller holding structured address COMPONENTS should use those instead —
 * they say which fragment is the street, which this cannot.
 */
export function splitDeliveryAddress(flat: string): string[] {
  const text = sanitizeTicketText(flat ?? '').trim();
  if (text.length === 0) return [];
  const fragments = text
    .split(',')
    .map((fragment) => fragment.trim())
    .filter((fragment) => fragment.length > 0);
  if (fragments.length <= 1) return fragments;
  return [...fragments.slice(0, -2), fragments.slice(-2).join(', ')];
}

/**
 * The lines a v2 `measure` block prints at a current column count of
 * `columns` — BEFORE the emitter's usual text step (sanitize, code page),
 * which each line then takes exactly as a `text` line does.
 *
 *   text     one line, every `{columns}` replaced by `columns` in decimal.
 *   overrun  the calibration ruler `columns + overrun` wide: a tens row
 *            ('1'..'9' under every tenth column, '+' past 99) and a units row.
 *
 * The ONE TypeScript copy — the preview's paperGrid prints through it. Go's
 * `measureLines` (apps/gateway document_v2.go) and Dart's `measureLines`
 * (apps/pos print_document_emitter.dart) are pinned to it by the
 * `measureLines` conformance op; at `overrun` 16 the ruler is the gateway's
 * legacy `calibrationRuler(width)`, which the `calibrationRuler` op pins.
 */
export function measureLines(block: PrintMeasureBlock, columns: number): string[] {
  if (block.overrun === undefined) {
    return [(block.text ?? '').split(PRINT_MEASURE_COLUMNS_TOKEN).join(String(columns))];
  }
  const n = columns + block.overrun;
  let tens = '';
  let units = '';
  for (let i = 1; i <= n; i += 1) {
    units += String(i % 10);
    if (i % 10 !== 0) tens += ' ';
    else if (i / 10 < 10) tens += String(i / 10);
    else tens += '+';
  }
  return [tens, units];
}
